// Contextual overrides for UI vocabulary that Google Translate renders with the
// wrong sense. The primary case is booking-domain "Book" / "Booking": Google
// translates these semantically to the Kannada word for a physical/written book
// (ಪುಸ್ತಕ) instead of the service-booking meaning users expect. Here we override
// such phrases with a transliteration ("ಬುಕ್" / "ಬುಕಿಂಗ್") while keeping the
// surrounding words in natural Kannada.
//
// This mirrors utils/skillTransliteration.js (which protects labour skill names).
// A phrase is only overridden when an entry exists for the active language;
// otherwise the caller lets Google Translate handle the text normally, so no
// other language regresses.

const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

const OVERRIDES_BY_LANG = {
  kn: {
    // Booking noun / navigation
    'book': 'ಬುಕ್',
    'booking': 'ಬುಕಿಂಗ್',
    'bookings': 'ಬುಕಿಂಗ್‌ಗಳು',
    'my bookings': 'ನನ್ನ ಬುಕಿಂಗ್‌ಗಳು',
    'total bookings': 'ಒಟ್ಟು ಬುಕಿಂಗ್‌ಗಳು',
    'recent bookings': 'ಇತ್ತೀಚಿನ ಬುಕಿಂಗ್‌ಗಳು',
    'more bookings': 'ಹೆಚ್ಚು ಬುಕಿಂಗ್‌ಗಳು',
    'view all bookings': 'ಎಲ್ಲಾ ಬುಕಿಂಗ್‌ಗಳನ್ನು ವೀಕ್ಷಿಸಿ',
    'back to bookings': 'ಬುಕಿಂಗ್‌ಗಳಿಗೆ ಹಿಂತಿರುಗಿ',
    'easy booking': 'ಸುಲಭ ಬುಕಿಂಗ್',
    'long distance booking': 'ದೂರದ ಬುಕಿಂಗ್',
    'select skill for booking': 'ಬುಕಿಂಗ್‌ಗಾಗಿ ಕೌಶಲ್ಯ ಆಯ್ಕೆಮಾಡಿ',
    // Booking actions
    'book now': 'ಈಗ ಬುಕ್ ಮಾಡಿ',
    'book worker': 'ಕೆಲಸಗಾರನನ್ನು ಬುಕ್ ಮಾಡಿ',
    'book personally': 'ವೈಯಕ್ತಿಕವಾಗಿ ಬುಕ್ ಮಾಡಿ',
    'book team': 'ತಂಡವನ್ನು ಬುಕ್ ಮಾಡಿ',
    'cancel booking': 'ಬುಕಿಂಗ್ ರದ್ದುಮಾಡಿ',
    'extend booking': 'ಬುಕಿಂಗ್ ವಿಸ್ತರಿಸಿ',
    // Schedule slot labels
    'booked by you': 'ನೀವು ಬುಕ್ ಮಾಡಿದ್ದೀರಿ',
  },
};

/**
 * Return the contextual override for a UI phrase in the given language, or null
 * when no override applies (unknown language/phrase, or English). When null, the
 * caller should let Google Translate handle the original text as usual.
 */
export function getUiTermOverride(text, rawLangCode) {
  const key = normalizeKey(text);
  if (!key) return null;
  const lang = (rawLangCode || '').toString().trim().toLowerCase().split('-')[0];
  if (!lang || lang === 'en') return null;
  const map = OVERRIDES_BY_LANG[lang];
  if (!map) return null;
  return map[key] || null;
}
